import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import "./StyledSelect.css";

function StyledSelect({
  value = "",
  options = [],
  onChange,
  placeholder = "Select",
  name,
  className = "",
  disabled = false,
  ariaLabel,
  title
}) {

  const [isOpen, setIsOpen] =
    useState(false);

  const rootRef =
    useRef(null);

  const triggerRef =
    useRef(null);


  const flatOptions =
    useMemo(() => {

      return options.flatMap(
        (item) =>
          Array.isArray(item?.options)
            ? item.options
            : [item]
      );

    }, [options]);


  const selectedOption =
    flatOptions.find(
      (option) =>
        String(option?.value ?? "") ===
        String(value ?? "")
    );


  useEffect(() => {

    if (!isOpen) {
      return undefined;
    }


    const handleOutsideClick =
      (event) => {

        if (
          rootRef.current &&
          !rootRef.current.contains(
            event.target
          )
        ) {

          setIsOpen(false);

        }

      };


    const handleKeyDown =
      (event) => {

        if (event.key === "Escape") {

          setIsOpen(false);

          triggerRef.current?.focus();

        }

      };


    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    document.addEventListener(
      "keydown",
      handleKeyDown
    );


    return () => {

      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );

      document.removeEventListener(
        "keydown",
        handleKeyDown
      );

    };

  }, [isOpen]);


  const chooseOption =
    (option) => {

      onChange?.({
        target: {
          name,
          value:
            String(
              option.value ?? ""
            )
        }
      });

      setIsOpen(false);

      requestAnimationFrame(
        () =>
          triggerRef.current?.focus()
      );

    };


  const renderOption =
    (option) => {

      const optionValue =
        String(
          option.value ?? ""
        );

      const selected =
        optionValue ===
        String(value ?? "");


      return (

        <button
          key={`${optionValue}-${option.label}`}
          type="button"
          className={`
            styled-select-option
            ${
              selected
                ? "styled-select-option-selected"
                : ""
            }
          `}
          role="option"
          aria-selected={selected}
          onClick={() =>
            chooseOption(option)
          }
        >

          <span>
            {option.label}
          </span>

          {selected && (
            <span
              className="styled-select-check"
              aria-hidden="true"
            >
              ✓
            </span>
          )}

        </button>

      );

    };


  return (

    <div
      ref={rootRef}
      className={`
        styled-select
        ${
          isOpen
            ? "styled-select-open"
            : ""
        }
        ${
          disabled
            ? "styled-select-disabled"
            : ""
        }
        ${className}
      `}
    >

      <button
        ref={triggerRef}
        type="button"
        className="styled-select-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={
          ariaLabel ||
          title ||
          placeholder
        }
        title={title}
        onClick={() =>
          setIsOpen(
            (current) => !current
          )
        }
      >

        <span
          className={
            selectedOption
              ? "styled-select-value"
              : "styled-select-placeholder"
          }
        >
          {selectedOption
            ? selectedOption.label
            : placeholder}
        </span>

        <span
          className="styled-select-chevron"
          aria-hidden="true"
        />

      </button>


      {isOpen && (

        <div
          className="styled-select-menu"
          role="listbox"
          aria-label={
            ariaLabel ||
            title ||
            placeholder
          }
        >

          {options.map(
            (item, index) => {

              if (
                Array.isArray(
                  item?.options
                )
              ) {

                return (

                  <div
                    className="styled-select-group"
                    key={
                      item.group ||
                      `group-${index}`
                    }
                  >

                    {item.group && (
                      <div
                        className="styled-select-group-label"
                      >
                        {item.group}
                      </div>
                    )}

                    {item.options.map(
                      renderOption
                    )}

                  </div>

                );

              }

              return renderOption(item);

            }
          )}

        </div>

      )}

    </div>

  );

}

export default StyledSelect;
